import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { LeafletMap, TileLayer } from '@/lib/leaflet/leafletMap';
import { mapyCzLayers } from '@/features/shared/tileLayers';
import { NgSwitch, PropertyWatcher } from '@/ui';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import { PositionSource } from '@/signalr';
import type { PermanentResidence, Position } from '@/signalr';
import {
  changeCallerLocationFromMap,
  changePreferredCallerLocationSource,
  openManualGpsCoordinatesDialog,
} from '../actions';
import { accuracyColor } from '../model/formatters';
import { getMapConfiguration } from '../model/psapConfiguration';
import { CONTEXT_MENU_CHANGE_CALLER_LOCATION } from './mapModel';
import type { ContextMenuItem, LatLng, MapCircle, MapPath } from './mapModel';
import { callerLocationMarker, createMarkerBySource, homeMarker } from './markers';
import type { MapMarker } from './markers';
import { LeafletMapView } from './LeafletMapView';
import { LeafletMapWithPopups } from './LeafletMapWithPopups';
import { MapLayerControl } from './MapLayerControl';
import { IncidentDetailMarkerFilters } from './IncidentDetailMarkerFilters';
import { PopupView } from './templates/PopupView';
import { CallerCurrentPositionMarkerDetailTemplate } from './templates/CallerCurrentPositionMarkerDetailTemplate';
import { CallerPermanentResidenceMarkerDetailTemplate } from './templates/CallerPermanentResidenceMarkerDetailTemplate';

export type IncidentDetailMapComponentProps = {
  incidentId: string;
  center?: { latitude: number; longitude: number } | null;
  zoom?: number | null;
  callerPosition: Position | null;
  callerPositionHistory: readonly Position[];
  permanentResidence?: PermanentResidence | null;
  onChangeCallerLocation?: (coordinates: { latitude: number; longitude: number }) => void | Promise<void>;
  incidentIsReadOnly: boolean;
};

type MapPopups = 'none' | 'homePosition' | 'callerPositionSource';

const CIRCLE_COLOR_GOOD = '#009A38';
const CIRCLE_COLOR_POOR = '#FFB500';
const CIRCLE_COLOR_USELESS = '#DE000F';

function getCircleByAccuracy(position: Position): MapCircle {
  const quality = accuracyColor(position.accuracy);
  const color =
    quality === 'excellent' ? CIRCLE_COLOR_GOOD : quality === 'warning' ? CIRCLE_COLOR_POOR : CIRCLE_COLOR_USELESS;

  return {
    latLng: { lat: position.latitude, lng: position.longitude },
    radius: position.accuracy,
    color,
    opacity: 0.5,
    fillOpacity: 0.2,
  };
}

export function IncidentDetailMapComponent({
  incidentId,
  center,
  zoom,
  callerPosition,
  callerPositionHistory,
  permanentResidence,
  onChangeCallerLocation,
  incidentIsReadOnly,
}: IncidentDetailMapComponentProps) {
  const { t, i18n } = useTranslation();
  const psapConfiguration = usePsapConfiguration();
  const mapConfiguration = getMapConfiguration(psapConfiguration);

  const leafletMapRef = useRef<LeafletMap | null>(null);

  const [openedPopup, setOpenedPopup] = useState<MapPopups>('none');
  const [showOtherLocationSourcesSwitch, setShowOtherLocationSourcesSwitch] = useState(false);

  const language = (i18n.language || 'en').split('-')[0] ?? 'en';
  const tileLayers: TileLayer[] = useMemo(
    () => mapyCzLayers(language, mapConfiguration.mapyCzApiKey, t),
    [language, mapConfiguration.mapyCzApiKey, t],
  );
  const [activeTileLayerId, setActiveTileLayerId] = useState(
    () => (tileLayers.find((layer) => layer.isDefault) ?? tileLayers[0])?.id ?? '',
  );
  const [visibleOverlayIds, setVisibleOverlayIds] = useState<ReadonlySet<string>>(
    () => new Set(mapConfiguration.layers.filter((layer) => layer.isDefaultVisible).map((layer) => layer.id)),
  );

  const callerPositionBySource = useMemo(() => {
    const bySource = new Map<PositionSource, Position>();
    for (const position of callerPositionHistory) {
      if (!bySource.has(position.positionSource)) {
        bySource.set(position.positionSource, position);
      }
    }
    return bySource;
  }, [callerPositionHistory]);

  const showOtherLocationSources =
    (callerPositionHistory.length > 1 && openedPopup === 'callerPositionSource') || showOtherLocationSourcesSwitch;
  const disableShowOtherLocationSources = callerPositionBySource.size <= 1;

  const markers: MapMarker[] = useMemo(() => {
    const result: MapMarker[] = [];

    if (permanentResidence?.geoPoint != null) {
      result.push(
        homeMarker('HomeMarker', {
          lat: permanentResidence.geoPoint.latitude,
          lng: permanentResidence.geoPoint.longitude,
        }),
      );
    }

    if (callerPosition !== null) {
      result.push(
        callerLocationMarker('CallerLocationMarker', { lat: callerPosition.latitude, lng: callerPosition.longitude }),
      );
    }

    if (showOtherLocationSources) {
      for (const positionBySource of callerPositionBySource.values()) {
        result.push(createMarkerBySource(positionBySource));
      }
    }

    return result;
  }, [permanentResidence, callerPosition, showOtherLocationSources, callerPositionBySource]);

  const circles: MapCircle[] = useMemo(() => {
    if (showOtherLocationSources) {
      return [...callerPositionBySource.values()].map(getCircleByAccuracy);
    }
    return callerPosition !== null ? [getCircleByAccuracy(callerPosition)] : [];
  }, [showOtherLocationSources, callerPositionBySource, callerPosition]);

  const paths: MapPath[] = useMemo(() => {
    const locationSource = callerPosition?.positionSource ?? null;
    if (!showOtherLocationSources && locationSource !== PositionSource.Manual) {
      const callerLocations: LatLng[] = callerPositionHistory
        .filter((position) => locationSource == null || position.positionSource === locationSource)
        .map((position) => ({ lat: position.latitude, lng: position.longitude }));

      const outerPathOptions = { weight: 7, stroke: true, color: '#444444', opacity: 0.7 };
      const innerPathOptions = { weight: 3, stroke: true, color: '#999999', opacity: 0.7 };

      return [
        { points: callerLocations, options: outerPathOptions },
        { points: callerLocations, options: innerPathOptions },
      ];
    }

    return [];
  }, [callerPosition, showOtherLocationSources, callerPositionHistory]);

  const contextMenuItems: ContextMenuItem[] = incidentIsReadOnly
    ? []
    : [{ id: CONTEXT_MENU_CHANGE_CALLER_LOCATION, text: t('Map_ContextMenuItem_SetCallerLocation') }];

  const closePopup = () => setOpenedPopup('none');

  const onMarkerClicked = (markerId: string) => {
    const marker = markers.find((candidate) => candidate.markerId === markerId);
    if (marker === undefined) {
      return;
    }

    const popup: MapPopups =
      marker.kind === 'callerLocation' || marker.positionSource !== undefined
        ? 'callerPositionSource'
        : marker.kind === 'home'
          ? 'homePosition'
          : 'none';

    setOpenedPopup(popup);
  };

  const changePreferredLocationSource = async (preferredLocationSource: PositionSource) => {
    await changePreferredCallerLocationSource(incidentId, preferredLocationSource);
  };

  const changeCallerLocation = async (latLng: LatLng) => {
    if (onChangeCallerLocation) {
      if (!disableShowOtherLocationSources) {
        setShowOtherLocationSourcesSwitch(true);
      }
      await onChangeCallerLocation({ latitude: latLng.lat, longitude: latLng.lng });
    } else {
      await changeCallerLocationFromMap(incidentId, { latitude: latLng.lat, longitude: latLng.lng });
    }
  };

  const openManualGpsEditDialog = () => {
    openManualGpsCoordinatesDialog(incidentId);
  };

  const toggleLocationSourcesSwitch = () => {
    if (showOtherLocationSources) {
      setShowOtherLocationSourcesSwitch(false);
      if (openedPopup === 'callerPositionSource') {
        setOpenedPopup('none');
      }
    } else {
      setShowOtherLocationSourcesSwitch(true);
    }
  };

  const centerMapOnCallerPosition = () => {
    const leafletMap = leafletMapRef.current;
    if (callerPosition !== null && leafletMap !== null) {
      let positions: Position[] = [callerPosition];
      if (showOtherLocationSources) {
        positions = positions.concat([...callerPositionBySource.values()]);
      }

      const defaultPadding: [number, number] = [50, 50];
      const paddingTopLeft: [number, number] = openedPopup === 'callerPositionSource' ? [350, 350] : defaultPadding;
      leafletMap.ensurePointsVisible(
        positions.map((position) => [position.latitude, position.longitude]),
        paddingTopLeft,
        defaultPadding,
      );
    }
  };

  const showOtherLocationSourcesChange = () => {
    if (showOtherLocationSources) {
      centerMapOnCallerPosition();
    }
  };

  const handleTileLayerChanged = (layerId: string) => {
    setActiveTileLayerId(layerId);
    leafletMapRef.current?.setActiveTileLayer(layerId);
  };

  const handleOverlayToggled = (id: string, visible: boolean) => {
    setVisibleOverlayIds((current) => {
      const next = new Set(current);
      if (visible) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

    leafletMapRef.current?.setOverlayVisibility(id, visible);
  };

  return (
    <>
      <LeafletMapWithPopups
        mapContent={
          <LeafletMapView
            onMapReady={(map) => {
              leafletMapRef.current = map;
            }}
            center={center}
            zoom={zoom}
            markers={markers}
            circles={circles}
            paths={paths}
            onMarkerClicked={onMarkerClicked}
            contextMenuItems={contextMenuItems}
            onChangeCallerLocation={(position) => void changeCallerLocation(position)}
            psapLayers={mapConfiguration.layers}
            tileLayers={tileLayers}
            hideBuiltInLayerControl
          />
        }
        popupContent={
          <>
            <PopupView show={openedPopup === 'callerPositionSource'} onClose={closePopup}>
              {callerPosition !== null && (
                <CallerCurrentPositionMarkerDetailTemplate
                  currentPosition={callerPosition}
                  positions={[...callerPositionBySource.values()]}
                  onSelect={changePreferredLocationSource}
                  allowSourceSwitching={!incidentIsReadOnly}
                  openManualPositionDialog={openManualGpsEditDialog}
                />
              )}
            </PopupView>

            <PopupView show={openedPopup === 'homePosition'} onClose={closePopup}>
              <CallerPermanentResidenceMarkerDetailTemplate item={permanentResidence} />
            </PopupView>
          </>
        }
      />

      <div className="position-absolute z-2 top-0 d-flex flex-row gap-1 m-2" style={{ right: '67px' }}>
        <IncidentDetailMarkerFilters>
          <NgSwitch
            label={t('IncidentDetail_ShowLocationSources')}
            onValueChanged={toggleLocationSourcesSwitch}
            value={showOtherLocationSources}
            disabled={disableShowOtherLocationSources}
          />
        </IncidentDetailMarkerFilters>

        <MapLayerControl
          tileLayers={tileLayers}
          overlayLayers={mapConfiguration.layers}
          activeTileLayerId={activeTileLayerId}
          onTileLayerChanged={handleTileLayerChanged}
          visibleOverlayIds={visibleOverlayIds}
          onOverlayToggled={handleOverlayToggled}
        />
      </div>

      <PropertyWatcher value={showOtherLocationSources} onChange={showOtherLocationSourcesChange} />
    </>
  );
}
